/**
 * Connection management utility functions
 */

import type {
  NangoConnection,
  ConnectionStatus,
  NangoProvider,
  ConnectionApiError,
  ConnectionHealth,
} from '../types/connections';
import {
  PROVIDER_CONFIGS,
  CONNECTION_STATUS_CONFIG,
  CONNECTION_ERROR_MESSAGES,
  CONNECTION_ERROR_CODES,
} from '../constants/connections';

/**
 * Substrings that indicate a connection limit error
 */
export const CONNECTION_LIMIT_ERROR_SUBSTRINGS = [
  'maximum number',
  'connection limits',
  'Upgrade your plan',
  'resource_capped',
] as const;

/**
 * Generic user-friendly message for connection limit errors
 * Does not expose technical details like "nango" or "resource_capped"
 */
export const CONNECTION_LIMIT_ERROR_MESSAGE =
  'We’re temporarily at maximum integration capacity. We’re expanding capacity and expect this to resolve shortly.';

/**
 * Check if an error message indicates a connection limit error
 * @param message - The error message to check
 * @returns True if the message indicates a connection limit error
 */
export function isConnectionLimitError(message?: string | null): boolean {
  if (!message) return false;
  return CONNECTION_LIMIT_ERROR_SUBSTRINGS.some((substring) => message.includes(substring));
}

/**
 * Get display configuration for a provider
 */
export function getProviderConfig(provider: NangoProvider) {
  return PROVIDER_CONFIGS[provider];
}

/**
 * Get display configuration for a connection status
 */
export function getStatusConfig(status: ConnectionStatus) {
  return CONNECTION_STATUS_CONFIG[status];
}

/**
 * Check if a connection is healthy (connected and recently verified)
 */
export function isConnectionHealthy(connection: NangoConnection): boolean {
  if (connection.status !== 'connected') {
    return false;
  }

  // Consider connection healthy if verified within the last hour
  const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
  return new Date(connection.last_verified) > oneHourAgo;
}

/**
 * Check if a connection needs attention (expired, error, or stale)
 */
export function connectionNeedsAttention(connection: NangoConnection): boolean {
  // Connection has an error
  if (connection.status === 'error' || connection.status === 'expired') {
    return true;
  }

  // Connection hasn't been verified in over 24 hours
  const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
  if (new Date(connection.last_verified) < oneDayAgo) {
    return true;
  }

  // Connection is expired
  if (connection.expires_at && new Date(connection.expires_at) < new Date()) {
    return true;
  }

  return false;
}

/**
 * Get a human-readable time since last activity
 */
export function getTimeSinceLastActivity(connection: NangoConnection): string {
  const lastActivity = connection.last_activity || connection.last_verified;
  const now = new Date();
  const activityDate = new Date(lastActivity);
  const diffMs = now.getTime() - activityDate.getTime();

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (minutes < 1) {
    return 'Just now';
  } else if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  } else if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  } else if (days < 30) {
    return `${days} day${days === 1 ? '' : 's'} ago`;
  } else {
    return activityDate.toLocaleDateString();
  }
}

/**
 * Format connection scopes for display
 */
export function formatConnectionScopes(scopes: string[]): string[] {
  return scopes.map((scope) => {
    // Convert technical scope names to user-friendly descriptions
    const scopeMap: Record<string, string> = {
      'https://www.googleapis.com/auth/drive.file': 'Access and modify files',
      'https://www.googleapis.com/auth/drive.readonly': 'Read files',
      'Files.ReadWrite': 'Read and write files',
      'Files.Read': 'Read files',
      'files.content.read': 'Read file contents',
      'files.content.write': 'Write file contents',
    };

    return scopeMap[scope] || scope;
  });
}

/**
 * Get user-friendly error message from error code
 */
export function getErrorMessage(error: ConnectionApiError | string): string {
  if (typeof error === 'string') {
    return CONNECTION_ERROR_MESSAGES[error] || error;
  }

  if (error.providerError) {
    return `${error.message} (${error.providerError.provider}: ${error.providerError.message})`;
  }

  return CONNECTION_ERROR_MESSAGES[error.code] || error.message;
}

/**
 * Calculate retry delay with exponential backoff
 */
export function calculateRetryDelay(attempt: number, baseDelay: number = 1000): number {
  return Math.min(baseDelay * Math.pow(2, attempt), 30000); // Max 30 seconds
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: ConnectionApiError): boolean {
  const retryableCodes = [
    CONNECTION_ERROR_CODES.NETWORK_ERROR,
    CONNECTION_ERROR_CODES.TIMEOUT,
    CONNECTION_ERROR_CODES.PROVIDER_UNAVAILABLE,
    CONNECTION_ERROR_CODES.RATE_LIMITED,
  ] as const;
  return error.retryable || retryableCodes.includes(error.code as (typeof retryableCodes)[number]);
}

/**
 * Sort connections by priority (errors first, then by last activity)
 */
export function sortConnectionsByPriority(connections: NangoConnection[]): NangoConnection[] {
  return [...connections].sort((a, b) => {
    // Connections needing attention come first
    const aNeedsAttention = connectionNeedsAttention(a);
    const bNeedsAttention = connectionNeedsAttention(b);

    if (aNeedsAttention && !bNeedsAttention) return -1;
    if (!aNeedsAttention && bNeedsAttention) return 1;

    // Then sort by last activity (most recent first)
    const aActivity = new Date(a.last_activity || a.last_verified).getTime();
    const bActivity = new Date(b.last_activity || b.last_verified).getTime();

    return bActivity - aActivity;
  });
}

/**
 * Filter connections by status
 */
export function filterConnectionsByStatus(
  connections: NangoConnection[],
  status: ConnectionStatus,
): NangoConnection[] {
  return connections.filter((conn) => conn.status === status);
}

/**
 * Get connections that need reconnection
 */
export function getConnectionsNeedingReconnection(
  connections: NangoConnection[],
): NangoConnection[] {
  return connections.filter(
    (conn) =>
      conn.status === 'expired' ||
      conn.status === 'error' ||
      (conn.expires_at && new Date(conn.expires_at) < new Date()),
  );
}

/**
 * Create a connection health summary
 */
export function createConnectionHealthSummary(connections: NangoConnection[]): ConnectionHealth {
  const totalConnections = connections.length;
  const connectionsNeedingAttention = connections.filter(connectionNeedsAttention).length;

  const healthy = totalConnections > 0 && connectionsNeedingAttention === 0;
  const lastChecked = new Date();

  return {
    healthy,
    lastChecked,
    responseTime: undefined, // This would be set by actual health check
    error: healthy ? undefined : `${connectionsNeedingAttention} connection(s) need attention`,
    nextCheck: new Date(Date.now() + 5 * 60 * 1000), // Next check in 5 minutes
  };
}

/**
 * Validate connection data
 */
export function validateConnection(connection: Partial<NangoConnection>): string[] {
  const errors: string[] = [];

  if (!connection.id) {
    errors.push('Connection ID is required');
  }

  if (!connection.connection_id) {
    errors.push('Nango connection ID is required');
  }

  if (!connection.provider || !Object.keys(PROVIDER_CONFIGS).includes(connection.provider)) {
    errors.push('Valid provider is required');
  }

  if (!connection.status || !Object.keys(CONNECTION_STATUS_CONFIG).includes(connection.status)) {
    errors.push('Valid status is required');
  }

  if (!connection.connected_at) {
    errors.push('Connected date is required');
  }

  if (!connection.last_verified) {
    errors.push('Last verified date is required');
  }

  if (!Array.isArray(connection.scopes)) {
    errors.push('Scopes must be an array');
  }

  return errors;
}

/**
 * Create a mock connection for testing
 */
export function createMockConnection(overrides: Partial<NangoConnection> = {}): NangoConnection {
  const now = new Date().toISOString();
  const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();

  return {
    id: 'mock-connection-id',
    connection_id: 'mock-nango-connection-id',
    provider: 'google-drive',
    provider_config_key: 'google-drive-config',
    status: 'connected',
    connected_at: oneHourAgo,
    last_verified: now,
    last_activity: now,
    scopes: ['https://www.googleapis.com/auth/drive.file'],
    is_active: true,
    ...overrides,
  };
}
